interface IValidationError {
  propertyName: string;
  errorMessages: string[];
}

interface IApiErrorResponse {
  statusCode: number;
  message: string;
  validationErrors?: IValidationError[];
  data?: string | { errors?: Record<string, string[]> };
}

export interface IParsedApiError {
  code: number;
  userMessage: string;
  toastType: "error" | "warning";
}

export function parseApiError(error: unknown): IParsedApiError {
  const defaultError: IParsedApiError = {
    code: 500,
    userMessage: "Ocurrió un error inesperado. Por favor, intenta nuevamente.",
    toastType: "error",
  };

  try {
    let errorResponse: IApiErrorResponse;

    if (typeof error === "string") {
      errorResponse = JSON.parse(error);
    } else if (error && typeof error === "object" && "error" in error) {
      const httpError = error as { error: unknown; status?: number };
      const innerError = httpError.error;
      if (typeof innerError === "string") {
        try {
          errorResponse = JSON.parse(innerError);
        } catch {
          errorResponse = {
            statusCode: httpError.status ?? 500,
            message: innerError,
          };
        }
      } else if (
        innerError &&
        typeof innerError === "object" &&
        "statusCode" in innerError
      ) {
        errorResponse = innerError as IApiErrorResponse;
      } else {
        errorResponse = {
          statusCode: httpError.status ?? 500,
          message:
            "Ocurrió un error inesperado. Por favor, intenta nuevamente.",
        };
      }
    } else {
      errorResponse = error as IApiErrorResponse;
    }

    const errorMessages: string[] = [];

    if (typeof errorResponse.data === "string") {
      errorMessages.push(errorResponse.data);
    } else if (errorResponse.data?.errors) {
      errorMessages.push("Ocurrió un error de validación.");
    } else if (
      errorResponse.validationErrors &&
      Array.isArray(errorResponse.validationErrors)
    ) {
      for (const validationError of errorResponse.validationErrors) {
        if (
          validationError.errorMessages &&
          Array.isArray(validationError.errorMessages)
        ) {
          errorMessages.push(...validationError.errorMessages);
        }
      }
    }

    if (errorMessages.length === 0 && errorResponse.message) {
      errorMessages.push(errorResponse.message);
    }

    const userMessage =
      errorMessages.length > 0
        ? errorMessages.join(" ")
        : defaultError.userMessage;

    const code = errorResponse.statusCode || 500;
    const toastType = code >= 500 ? "error" : "warning";

    return {
      code,
      userMessage,
      toastType,
    };
  } catch {
    return defaultError;
  }
}

export async function parseApiErrorAsync(error: unknown) {
  if (error && typeof error === "object" && "error" in error) {
    const httpError = error as { error: unknown };
    if (httpError.error instanceof Blob) {
      try {
        const text = await httpError.error.text();
        return parseApiError({ ...httpError, error: text });
      } catch {
        return parseApiError(error);
      }
    }
  }
  return parseApiError(error);
}

export function generateDownload(file: Blob | string, fileName: string) {
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style.display = "none";
  const url = file instanceof Blob ? window.URL.createObjectURL(file) : file;
  a.href = url;
  a.download = fileName;
  a.click();

  if (file instanceof Blob) {
    window.URL.revokeObjectURL(url);
  }
  document.body.removeChild(a);
}
